import { HeightOutlined, Refresh, Search } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button, FormHelperText, Grid, InputLabel, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ReactMultiEmail } from 'react-multi-email';
import { getActiveCampaignDetails, retailerSearch, skuSearch } from '../../service/Service';
import { AppContext } from '../../store/AppProvider';
import { SHOW_ERROR_ALERT, SHOW_WARNING_ALERT } from '../../store/reducer/AlertNotifierReducer';
import { ErrorConstant, regSources, Warnings } from '../../utils/Constants';
import { addFutureDateByMonth, convertDate, isDate, isEmptyObj, withoutTime } from '../../utils/Utils';
import { CustomFormField } from '../component';
import { FieldTypes } from '../CustomFormField/CustomFormField';

export const RegistrationSearchForm = ({ onSearch, onResetClick = () => { }, loading = false, buttonText = "Search",
    buttonIcon = null, showPopField = true, maxPurchaseDateMonthCount = 0, maxRegDateMonthCount = 0, maxWarrantyDateMonthCount = 0 }) => {
    const { regSearchParams, setRegSearchParams, setAlert, setShowLoader } = useContext(AppContext)
    const {
        first_name: firstName,
        last_name: lastName,
        reg_id_search: registrationId,
        serial_number: serialNumber,
        locale: regSearchLocale,
        email: regSearchEmail,
        purchase_date_to: purchaseDateTo,
        purchase_date_from: purchaseDateFrom,
        warranty_date_to: warrantyDateTo,
        warranty_date_from: warrantyDateFrom,
        register_date_to: registerDateTo,
        register_date_from: registerDateFrom,
        product_sku: productSku,
        source: regSearchSource,
        purchased_at: purchaseAt,
        proof_of_purchase: proofOfPurchase,
        entitled: isEntitled,
        optin: optinVal,
        campaign_id: selectedCampaign
    } = regSearchParams

    const [campaignOptions, setCampaignOptions] = useState([])

    const {
        handleSubmit,
        reset,
        control,
        watch,
        setValue,
        resetField,
        setError,
        formState: { errors, dirtyFields, touchedFields }
    } = useForm({
        defaultValues: {
            first_name: firstName || "",
            last_name: lastName || "",
            reg_id_search: registrationId || "",
            // serial_number: serialNumber || "",
            locale: regSearchLocale || [],
            purchase_date_to: Boolean(purchaseDateTo) ? dayjs(purchaseDateTo) : null,
            purchase_date_from: Boolean(purchaseDateFrom) ? dayjs(purchaseDateFrom) : null,
            warranty_date_to: Boolean(warrantyDateTo) ? dayjs(warrantyDateTo) : null,
            warranty_date_from: Boolean(warrantyDateFrom) ? dayjs(warrantyDateFrom) : null,
            register_date_to: Boolean(registerDateTo) ? dayjs(registerDateTo) : null,
            register_date_from: Boolean(registerDateFrom) ? dayjs(registerDateFrom) : null,
            product_sku: productSku || "",
            source: regSearchSource || [],
            purchased_at: purchaseAt || "",
            proof_of_purchase: proofOfPurchase || "",
            entitled: isEntitled || "",
            optin: optinVal || "all",
            campaign_id: selectedCampaign || ""
        }
    });

    const selectedPurchaseDateFrom = watch('purchase_date_from')
    const selectedWarrantyEndDateFrom = watch('warranty_date_from')
    const selectedRegisterDateFrom = watch('register_date_from')

    useEffect(() => {
        const { purchase_date_from, warranty_date_from, register_date_from } = touchedFields
        if (!selectedPurchaseDateFrom && purchase_date_from) {
            setValue('purchase_date_to', null)
        }
        if (!selectedWarrantyEndDateFrom && warranty_date_from) {
            setValue('warranty_date_to', null)
        }
        if (!selectedRegisterDateFrom && register_date_from) {
            setValue('register_date_to', null)
        }
    }, [selectedPurchaseDateFrom, selectedWarrantyEndDateFrom, selectedRegisterDateFrom, setValue, touchedFields])

    useEffect(() => {
        const fetchCampaignList = async () => {
            try {
                setShowLoader(true)
                const data = await getActiveCampaignDetails()
                if (data?.length) {
                    const campaignList = data?.map(ele => ele?.campaign_id)
                    setCampaignOptions(campaignList)
                }
            } catch (error) {
                setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchCampaignDetails })
            } finally {
                setShowLoader(false)
            }
        }
        fetchCampaignList()
    }, [setAlert, setShowLoader])

    const resetForm = () => {
        setRegSearchParams({})
        if (emails?.length) setEmails([])
        if (serialNos?.length) setSerialNos([])
        reset({
            reg_id_search: "",
            first_name: "",
            last_name: "",
            // serial_number: "",
            locale: [],
            purchase_date_to: null,
            purchase_date_from: null,
            warranty_date_to: null,
            warranty_date_from: null,
            register_date_to: null,
            register_date_from: null,
            product_sku: "",
            source: [],
            purchased_at: "",
            proof_of_purchase: "",
            entitled: "",
            campaign_id: ""
        })
        onResetClick()
    }

    const [searchResults, setSearchResults] = useState([]);
    const [retailerResults, setRetailerResults] = useState([]);
    const [typingTimeout, setTypingTimeout] = useState(0);
    const [emails, setEmails] = useState(regSearchEmail || [])
    const [emailsError, setEmailsError] = useState('')
    const [serialNos, setSerialNos] = useState(serialNumber || [])
    const [serialNosError, setSerialNosError] = useState('')

    const handleProductSearch = async (event) => {
        const searchText = event.target.value;
        // Clear the previous timeout
        clearTimeout(typingTimeout);

        // Set a new timeout
        const timeoutId = setTimeout(async () => {
            if (searchText.length > 2) {
                try {
                    const fetchedData = await skuSearch(searchText);
                    setSearchResults(fetchedData);
                } catch (error) {
                    setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchProducts })
                }
            }
        }, 300); // Debounce time in milliseconds

        setTypingTimeout(timeoutId);
    };

    const handleRetailerSearch = async (event) => {
        const searchText = event.target.value;
        clearTimeout(typingTimeout);

        // Set a new timeout
        const timeoutId = setTimeout(async () => {
            if (searchText.length > 2) {
                try {
                    const fetchedData = await retailerSearch(searchText);
                    setRetailerResults(fetchedData);
                } catch (error) {
                    setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchRetailers })

                }
            }
        }, 300); // Debounce time in milliseconds

        setTypingTimeout(timeoutId);
    }

    const resetDateFields = (formData) => {
        const {
            purchase_date_from,
            purchase_date_to,
            warranty_date_from,
            warranty_date_to,
            register_date_from,
            register_date_to } = formData
        if (!purchase_date_from) {
            formData.purchase_date_to = null
            resetField('purchase_date_to')
        }
        else {
            formData.purchase_date_to = convertDate(purchase_date_to)
        }
        if (!warranty_date_from) {
            formData.warranty_date_to = null
            resetField('warranty_date_to')
        } else {
            formData.warranty_date_to = convertDate(warranty_date_to)
        }
        if (!register_date_from) {
            formData.register_date_to = null
            resetField('register_date_to')
        } else {
            formData.register_date_to = convertDate(register_date_to)
        }
    }

    const notValidData = (formData) => {
        const {
            purchase_date_from,
            purchase_date_to,
            warranty_date_from,
            warranty_date_to,
            register_date_from,
            register_date_to } = formData
        let isNotValid = false;
        if (Boolean(purchase_date_from)) {
            if (!isDate(purchase_date_from)) {
                isNotValid = true
                setError('purchase_date_from', { type: 'custom', message: 'Wrong date format' })
            }
            if (!Boolean(purchase_date_to)) {
                isNotValid = true
                setError('purchase_date_to', { type: 'required', message: 'To date is required' })
            } else {
                if (isDate(purchase_date_to)) {
                    if ((withoutTime(purchase_date_to) < withoutTime(purchase_date_from))) {
                        setError('purchase_date_to', { type: 'custom', message: 'Purchase Date To cannot be past date' })
                        isNotValid = true
                    }
                }
                else {
                    isNotValid = true
                    setError('purchase_date_to', { type: 'custom', message: 'Wrong date format' })
                }
            }

        }
        if (Boolean(warranty_date_from)) {
            if (!isDate(warranty_date_from)) {
                isNotValid = true
                setError('warranty_date_from', { type: 'custom', message: 'Wrong date format' })
            }
            if (!Boolean(warranty_date_to)) {
                isNotValid = true
                setError('warranty_date_to', { type: 'required', message: 'To Date is required' })
            } else {
                if (isDate(warranty_date_to)) {
                    if (withoutTime(warranty_date_to) < withoutTime(warranty_date_from)) {
                        setError('warranty_date_to', { type: 'custom', message: 'Warranty Date To cannot be past date' })
                        isNotValid = true
                    }
                } else {
                    isNotValid = true
                    setError('warranty_date_to', { type: 'custom', message: 'Wrong date format' })
                }
            }
        }
        if (Boolean(register_date_from)) {
            if (!isDate(register_date_from)) {
                isNotValid = true
                setError('register_date_from', { type: 'custom', message: 'Wrong date format' })
            }
            if (!Boolean(register_date_to)) {
                isNotValid = true
                setError('register_date_to', { type: 'required', message: 'To date is required' })
            } else {
                if (isDate(register_date_to)) {
                    if ((withoutTime(register_date_to) < withoutTime(register_date_from))) {
                        setError('register_date_to', { type: 'custom', message: 'Register Date To cannot be past date' })
                        isNotValid = true
                    }
                } else {
                    isNotValid = true
                    setError('register_date_to', { type: 'custom', message: 'Wrong date format' })
                }
            }
        }
        return isNotValid;
    }

    const handleSearch = async (formData) => {
        const {
            purchase_date_from,
            warranty_date_from,
            register_date_from,
            locale,
            source,
            proof_of_purchase,
            entitled, optin } = formData
        resetDateFields(formData)
        formData.purchase_date_from = convertDate(purchase_date_from)
        formData.warranty_date_from = convertDate(warranty_date_from)
        formData.register_date_from = convertDate(register_date_from)
        formData.locale = locale || []
        formData.source = source || []
        formData.proof_of_purchase = showPopField ? (proof_of_purchase || 'all') : null
        formData.entitled = entitled || 'all'
        formData.optin = optin || 'all'
        formData.email = emails
        formData.serial_number = serialNos
        // formData.campaign_id = formData?.campaign_id === 'DEFAULT' ? '' : formData?.campaign_id
        if (isEmptyObj(regSearchParams)) {
            if (isEmptyObj(dirtyFields) && !emails?.length && !serialNos?.length) {
                setAlert({ type: SHOW_WARNING_ALERT, alertMessage: Warnings.selectOneFilter })
                return;
            }
        }
        if (notValidData(formData)) {
            return
        }
        setRegSearchParams(formData)
        onSearch(formData)
    }

    const onError = (formData) => { }

    return (
        <>
            <form
                onSubmit={handleSubmit(handleSearch, onError)}
                noValidate
                autoComplete="off">
                <Grid container columnSpacing={8} marginBottom={1} >
                    <Grid item xs={12} md={4}>
                        <CustomFormField label='First Name' textFieldType='search' control={control} name={"first_name"} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <CustomFormField label='Last Name' textFieldType='search' control={control} name={"last_name"} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <>
                            <InputLabel error={Boolean(emailsError)} shrink htmlFor={'email'} style={{ fontSize: 20, fontWeight: 'bold' }}>
                                {'Email'}
                            </InputLabel>
                            <ReactMultiEmail
                                placeholder={''}
                                emails={emails}
                                onChange={(_emails) => {
                                    if (_emails.length <= 5) {
                                        setEmails(_emails)
                                        if (Boolean(emailsError)) {
                                            setEmailsError('')
                                        }
                                    } else {
                                        setEmailsError('maximum limit reached!')
                                    }
                                }}
                                getLabel={(
                                    email,
                                    index,
                                    removeEmail
                                ) => {
                                    return (
                                        <div data-tag key={index}>
                                            {email}
                                            <span data-tag-handle onClick={() => removeEmail(index)}>
                                                ×
                                            </span>
                                        </div>
                                    );
                                }}
                            />
                            <FormHelperText error={Boolean(emailsError)}>{emailsError}</FormHelperText>
                        </>
                    </Grid>
                </Grid>
                
                <Grid container columnSpacing={8} marginBottom={1} >
                    <Grid item xs={12} md={4}>
                        <InputLabel shrink htmlFor='purchased_at' style={{ fontSize: 20, fontWeight: 'bold' }}>Purchased At</InputLabel>
                        <Controller
                            name="purchased_at"
                            control={control}
                            render={({ field: { onChange, value: currValue } }) =>
                            (
                                <Autocomplete
                                    freeSolo
                                    disableClearable
                                    options={retailerResults}
                                    sx={{
                                        "& .MuiInputBase-root": { height: "35px", backgroundColor: 'white' },
                                    }}
                                    getOptionLabel={(option) => option?.retailer_name ?? ""}
                                    onInputChange={(event, newValue, reason) => {
                                        onChange(newValue);
                                        setRetailerResults([])
                                    }}
                                    inputValue={currValue}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            id="purchased_at"
                                            InputLabelProps={{ shrink: true }}
                                            onChange={handleRetailerSearch}
                                            error={false}
                                            value={currValue}
                                        />)} />
                            )
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Grid container >
                        <Grid item xs={12} md={5.5}>
                        <CustomFormField label='Reg ID' textFieldType='search' control={control} name={"reg_id_search"} />
                    </Grid>
                    <Grid item xs={12} md={1} />
                        <Grid item xs={12} md={5.5}>
                        <InputLabel shrink htmlFor='product_sku' style={{ fontSize: 20, fontWeight: 'bold' }}>Product SKU</InputLabel>
                        <Controller
                            name="product_sku"
                            control={control}
                            render={({ field: { onChange, value: currVal } }) => (
                                <Autocomplete
                                    freeSolo
                                    disableClearable
                                    onInputChange={(event, newValue, reason) => {
                                        onChange(newValue);
                                        setSearchResults([])
                                    }}
                                    options={searchResults}
                                    sx={{
                                        "& .MuiInputBase-root": { height: "35px", backgroundColor: 'white' },
                                    }}
                                    getOptionLabel={(option) => option?.sku_name}
                                    inputValue={currVal}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            id="product_sku"
                                            InputLabelProps={{ shrink: true }}
                                            error={false}
                                            value={currVal || ''}
                                            onChange={handleProductSearch}
                                        />
                                    )}
                                />)}
                        />
                        </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <>
                            <InputLabel error={Boolean(serialNosError)} shrink htmlFor={'serial_number'} style={{ fontSize: 20, fontWeight: 'bold' }}>
                                {'Serial Number'}
                            </InputLabel>
                            <ReactMultiEmail
                                placeholder={''}
                                validateEmail={(serial) => serial?.length > 3 ? true : false}
                                emails={serialNos}
                                onChange={(_serials) => {
                                    if (_serials.length <= 5) {
                                        setSerialNos(_serials)
                                        if (Boolean(serialNosError)) {
                                            setSerialNosError('')
                                        }
                                    } else {
                                        setSerialNosError('maximum limit reached!')
                                    }
                                }}
                                getLabel={(
                                    serial,
                                    index,
                                    removeSerial
                                ) => {
                                    return (
                                        <div data-tag key={index}>
                                            {serial}
                                            <span data-tag-handle onClick={() => removeSerial(index)}>
                                                ×
                                            </span>
                                        </div>
                                    );
                                }}
                            />
                            <FormHelperText error={Boolean(serialNosError)}>{serialNosError}</FormHelperText>
                        </>
                        {/* <CustomFormField label='Serial Number' control={control} name={"serial_number"} /> */}
                    </Grid >
                </Grid >
                <Grid container columnSpacing={8} marginBottom={1} >
                    <Grid item xs={12} md={4}>
                        <InputLabel error={Boolean(errors?.purchase_date_from?.message || errors?.purchase_date_to?.message)} shrink style={{ fontSize: 20, fontWeight: 'bold' }}>Purchased Between</InputLabel>
                        <Grid container >
                            <Grid item xs={12} md={5} >
                                <CustomFormField errorMessage={errors?.purchase_date_from?.message} type={FieldTypes.date} fontSize={11} name="purchase_date_from" control={control} />
                            </Grid>
                            <Grid item xs={12} md={2}  >
                                <HeightOutlined style={{ width: '100%', color: 'rgba(0, 0, 0,0.6)', transform: 'rotate(90deg)' }} fontSize='large' />
                            </Grid>
                            <Grid item xs={12} md={5} >
                                <CustomFormField
                                    errorMessage={errors?.purchase_date_to?.message}
                                    type={FieldTypes.date}
                                    fontSize={11}
                                    minDate={selectedPurchaseDateFrom}
                                    maxDate={maxPurchaseDateMonthCount ? addFutureDateByMonth(selectedPurchaseDateFrom, maxPurchaseDateMonthCount) : null}
                                    disabled={!selectedPurchaseDateFrom}
                                    name="purchase_date_to"
                                    control={control} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <InputLabel error={Boolean(errors?.warranty_date_from?.message || errors?.warranty_date_to?.message)} shrink style={{ fontSize: 20, fontWeight: 'bold' }}>Warranty End Between</InputLabel>
                        <Grid container >
                            <Grid item xs={12} md={5} >
                                <CustomFormField errorMessage={errors?.warranty_date_from?.message} type={FieldTypes.date} fontSize={11} name="warranty_date_from" control={control} />
                            </Grid>
                            <Grid item xs={12} md={2} >
                                <HeightOutlined style={{ width: '100%', color: 'rgba(0, 0, 0,0.6)', transform: 'rotate(90deg)' }} fontSize='large' />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <CustomFormField
                                    errorMessage={errors?.warranty_date_to?.message}
                                    type={FieldTypes.date}
                                    fontSize={11}
                                    minDate={selectedWarrantyEndDateFrom}
                                    maxDate={maxWarrantyDateMonthCount ? addFutureDateByMonth(selectedWarrantyEndDateFrom, maxRegDateMonthCount) : null}
                                    disabled={!selectedWarrantyEndDateFrom}
                                    name="warranty_date_to"
                                    control={control} />
                            </Grid>
                        </Grid >
                    </Grid >
                    <Grid item xs={12} md={4}>
                        <InputLabel error={Boolean(errors?.register_date_from?.message || errors?.register_date_to?.message)} shrink style={{ fontSize: 20, fontWeight: 'bold' }}>Registered Between</InputLabel>
                        <Grid container >
                            <Grid item xs={12} md={5} >
                                <CustomFormField errorMessage={errors?.register_date_from?.message} type={FieldTypes.date} fontSize={11} name="register_date_from" control={control} />
                            </Grid>
                            <Grid item xs={12} md={2} >
                                <HeightOutlined style={{ width: '100%', color: 'rgba(0, 0, 0,0.6)', transform: 'rotate(90deg)' }} fontSize='large' />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <CustomFormField
                                    errorMessage={errors?.register_date_to?.message}
                                    type={FieldTypes.date}
                                    fontSize={11}
                                    minDate={selectedRegisterDateFrom}
                                    maxDate={maxRegDateMonthCount ? addFutureDateByMonth(selectedRegisterDateFrom, maxRegDateMonthCount) : null}
                                    disabled={!selectedRegisterDateFrom}
                                    name="register_date_to"
                                    control={control} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid >
                <Grid container columnSpacing={8} marginBottom={1} >
                    <Grid item xs={12} md={4}>
                        <Grid container >
                            <Grid item xs={12} md={5.5}>
                                <CustomFormField label='Source' name="source" type={FieldTypes.multiSelect} control={control} options={regSources} placeholder='source' />
                            </Grid>
                            <Grid item xs={12} md={1} />
                            <Grid item xs={12} md={5.5}>
                                <CustomFormField control={control} label='Email Optin' name={"optin"} type={FieldTypes.select} options={['all', 'yes', 'no']} />
                            </Grid>
                        </Grid>
                    </Grid >
                    <Grid item xs={12} md={4}>
                        <Grid container >
                            <Grid item xs={12} md={5.5}>
                                <CustomFormField label='Locale' name="locale" type={FieldTypes.multiSelect} control={control} options={['en_US', 'en_CA', 'fr_CA']} placeholder='Locale' />
                                {/* <CustomFormField label='Locale' control={control} name={"locale"} type='select' options={['all', 'en_US', 'en_CA', 'fr_CA']} /> */}
                            </Grid>
                            <Grid item xs={12} md={1} />
                            <Grid item xs={12} md={5.5}>
                                <CustomFormField label='Campaign' control={control} name={"campaign_id"} type='select' options={[...campaignOptions]} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={4} >
                        <Grid container >
                            {showPopField
                                ? <>
                                    <Grid item xs={12} md={6}>
                                        <CustomFormField control={control} label='Proof of Purchase' name={"proof_of_purchase"} type={FieldTypes.select} options={['all', 'yes', 'no']} />
                                    </Grid>
                                    <Grid item xs={12} md={1} />
                                </>
                                : null}
                            <Grid item xs={12} md={showPopField ? 5 : 12}>
                                <CustomFormField control={control} label='Entitled?' name={"entitled"} type={FieldTypes.select} options={['all', 'yes', 'no']} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 25 }}>
                    <LoadingButton
                        loading={loading}
                        loadingPosition={"start"}
                        startIcon={buttonIcon || <Search />}
                        variant="contained"
                        type='submit'
                        size="medium"
                        style={{ minWidth: '12%' }}
                    >
                        {buttonText}
                    </LoadingButton>
                    <Button startIcon={<Refresh />} variant="outlined" size="medium" style={{ marginLeft: 20, minWidth: '12%', backgroundColor: 'white' }} onClick={() => resetForm()}>Reset</Button >
                </div>
            </form >
        </>
    )
}
